/**
 * Extraction grabs the first capture group and skips group 0. So a pattern with no parentheses
 * matches perfectly, hands back nothing, and the field shows up empty forever.
 * "Inbound|Outbound" needs to be "(Inbound|Outbound)".
 *
 * Nothing warned anyone about this, so here we are.
 */

/** A real group: "(" not followed by "?". Anything starting "(?" is (?:...), (?=...) and friends. */
const CAPTURING_GROUP = /\((?!\?)/;

/** "\(" is just a paren character, not the start of a group. */
const ESCAPED = /\\./g;

export const hasCapturingGroup = (pattern: string): boolean => {
    // Named groups (?<name>...) do capture. Everything else starting "(?" does not.
    const withoutEscapes = pattern.replace(ESCAPED, "");
    return CAPTURING_GROUP.test(withoutEscapes) || /\(\?<[^=!]/.test(withoutEscapes);
};

export const isValidRegex = (pattern: string): boolean => {
    try {
        // eslint-disable-next-line no-new
        new RegExp(pattern);
        return true;
    } catch {
        return false;
    }
};

/**
 * A message when the pattern will not do what you think it does, otherwise null.
 *
 * A warning, not a blocker */
export const getExtractionPatternWarning = (
    pattern: string | null | undefined,
): string | null => {
    if (!pattern || pattern.trim() === "") {
        return null;
    }

    if (!isValidRegex(pattern)) {
        return "This is not a valid regular expression, so no value will be extracted.";
    }

    if (!hasCapturingGroup(pattern)) {
        return "Add a capture group or this field will come through empty, for example (Inbound|Outbound), not Inbound|Outbound.";
    }

    return null;
};

/**
 * Where the pattern actually applies, since it is not obvious and the answer is inconsistent.
 * Mapped to a target: yes. Compared in a condition: yes. Used as a condition result: no, because the
 * result only stores the path and the raw value comes through.
 */
export const EXTRACTION_PATTERN_HELP =
    "Applies when this field is mapped to a target or compared in a condition. Not applied when it is used as a condition result.";
